import { LOCAL_STORAGE_KEYS } from '../consts/localStorage';
import { QODO_20_BANNER_STORAGE_KEY } from '../layouts/Main/consts';
import { OIDCAuthenticationClient } from '../network/httpClients/OIDCAuthenticationClient';
import { OIDCError } from '../network/httpClients/OIDCAuthenticationClient/api.schema';
import {
  getInitClientQueryParam,
  getV2RedirectURI,
} from '../pages/Login/utils';
import { ROUTE_PATHS, QUERY_PARAMS } from '../routes/consts';
import { removeAccessTokenCookie } from './cookies';
import { getAppUrl } from './dynamicSettings';
import { isAirgappedMode } from './env';
import { getTrustedPortalRedirectUrl } from './portalRedirect';
import { getAccessToken, isCrossDomainRedirectActive } from './token';
import {
  goToPathname,
  openUrl,
  buildURLWithQueryParams,
  getOIDCAuthorizeEndpointUrl,
  getTraceIdFromSessionStorage,
  getSessionIdFromSessionStorage,
  getQueryParam,
  buildSearchParams,
} from './url';

/**
 * List of localStorage keys that should be preserved after signout
 * Add any keys here that need to persist across user sessions
 */
const STORAGE_KEYS_TO_PRESERVE = [
  QODO_20_BANNER_STORAGE_KEY,
  LOCAL_STORAGE_KEYS.ONBOARDING_WIZARD_COMPLETED,
  LOCAL_STORAGE_KEYS.LAST_USED_AUTH_PROVIDER,
];

/**
 * Type guard to check if an error is of type OIDCError
 * @param err - The error to check
 * @returns true if the error is of type OIDCError, false otherwise
 */
export const isOIDCError = (err: unknown): err is OIDCError =>
  typeof err === 'object' &&
  err !== null &&
  'error' in err &&
  typeof (err as any).error === 'object' &&
  (err as any).error !== null &&
  'internal_status_code' in (err as any).error &&
  typeof (err as any).error.internal_status_code === 'string' &&
  'message' in (err as any).error;

/**************
  Get auth
 **************/
export const getOIDCToken = (): Promise<string | undefined> => {
  const token = getAccessToken();

  return Promise.resolve(token);
};

/**************
  Signout 
 **************/
/**
 * signout function for the shared logic that should be used for both legacy and OIDC authentication flows.
 * Each store is cleared independently: Web Storage can throw (blocked/disabled/
 * quota), and a failure in one must not abort the rest of the cleanup or the
 * sign-out redirect.
 */
const executeSharedSignoutLogic = () => {
  try {
    const preserved = STORAGE_KEYS_TO_PRESERVE.flatMap((key) => {
      const value = localStorage.getItem(key);

      return value ? [[key, value]] : [];
    });

    localStorage.clear();

    preserved.forEach(([key, value]) => {
      localStorage.setItem(key, value);
    });
  } catch (error) {
    console.error('Failed to clear localStorage during signout:', error);
  }

  try {
    sessionStorage.clear();
  } catch (error) {
    console.error('Failed to clear sessionStorage during signout:', error);
  }

  removeAccessTokenCookie();

  // Trust portal_redirect_url only via the shared guard: on a customer-owned
  // (onprem/airgapped) domain the field may carry the inherited multi-tenant
  // default, and sign-out must stay on the deployment's own portal.
  const signoutUrl =
    getTrustedPortalRedirectUrl()?.toString() ??
    `${getAppUrl()}/${ROUTE_PATHS.SIGNIN}`;

  goToPathname(signoutUrl);
};

/**
 * Sign out the user in the OIDC authentication flow.
 *
 * No-ops while a cross-domain 409 handoff is in flight. In that window the credentials live
 * only in the shared-domain cookie, on their way to the portal we are navigating to — so both
 * halves of a sign-out are destructive: end_session revokes the grant at the provider, and the
 * local cleanup deletes the cookie the destination is about to read. Guarding here rather than
 * at each trigger means every path (the refresh interceptor, the user menu, self-deletion, and
 * anything added later) is covered without having to know about the handoff.
 */
export const oidcSignout = () => {
  if (isCrossDomainRedirectActive()) {
    console.info('Skipping sign-out: a cross-domain auth handoff is in flight');

    return;
  }

  if (isAirgappedMode()) {
    executeSharedSignoutLogic();

    return;
  }

  OIDCAuthenticationClient.endSession()
    .then(() => {
      executeSharedSignoutLogic();
    })
    .catch((error) => {
      console.error('Failed to end session: ', error);
      handleOIDCError(error);
      executeSharedSignoutLogic();
    });
};

/**
 * Handles OIDC error redirection by extracting the internal status code and redirecting to the error page
 * @param err - The error to handle
 */
export const handleOIDCError = (err: unknown): void => {
  const internalStatusCode = isOIDCError(err)
    ? err.error.internal_status_code
    : 'FALLBACK';

  const url = buildURLWithQueryParams(`${getAppUrl()}/${ROUTE_PATHS.ERROR}`, {
    [QUERY_PARAMS.AUTH_V2]: 'true',
    [QUERY_PARAMS.INTERNAL_STATUS_CODE]: internalStatusCode,
  });

  openUrl(url);
};

/**
 * Reusable function for making fetch calls to the /authorize endpoint
 * @param additionalParams - Additional URLSearchParams to append to the authorize URL (optional)
 * @param options - Optional configuration for the fetch request (native RequestInit)
 * @returns Promise that resolves to the response data or throws an error
 */
export const fetchAuthorizeEndpoint = async (
  additionalParams?: URLSearchParams,
  options: RequestInit = {},
): Promise<Response> => {
  const { headers = {}, ...restOptions } = options;
  const originalMethod = getQueryParam(QUERY_PARAMS.METHOD);
  const originalReturnUrl = getQueryParam(QUERY_PARAMS.RETURN_URL);

  // Build shared params that are common to all authorize endpoint calls
  const redirectUri = getV2RedirectURI();
  const sharedParams = buildSearchParams({
    [QUERY_PARAMS.REDIRECT_TO]: redirectUri,
    [QUERY_PARAMS.REDIRECT_URI]: redirectUri,
    [QUERY_PARAMS.SESSION_ID]: getSessionIdFromSessionStorage() || '',
    [QUERY_PARAMS.TRACE_ID]: getTraceIdFromSessionStorage(),
    [QUERY_PARAMS.INIT_CLIENT]: getInitClientQueryParam(),
  });

  if (originalMethod) {
    sharedParams.set(QUERY_PARAMS.METHOD, originalMethod);
  }

  if (originalReturnUrl) {
    sharedParams.set(QUERY_PARAMS.RETURN_URL, originalReturnUrl);
  }

  if (additionalParams) {
    for (const [key, value] of additionalParams.entries()) {
      sharedParams.append(key, value);
    }
  }

  const url = getOIDCAuthorizeEndpointUrl(sharedParams);

  const fetchOptions: RequestInit = {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      ...headers,
    },
    ...restOptions,
  };

  const response = await fetch(url, fetchOptions);

  if (!response.ok && response.type !== 'opaqueredirect') {
    const errorData = await response.json();

    throw errorData;
  }

  return response;
};
